<template>
  <div class="endScreen">
    <div class="endInner">
      <div class="endIcon" :class="variant === 'nice' ? 'ok' : 'err'">
        <AppIcon :name="variant === 'nice' ? 'checkmark' : 'error'" :size="26" />
      </div>
      <h1 class="endTitle">{{ copy.title }}</h1>
      <p class="endSub">{{ copy.sub }}</p>
      <SessionStatTiles v-if="variant !== 'error_before'" :stats="stats" class="endTiles" />
      <ErrorDetailBox v-if="errorDetail" :detail="errorDetail" class="endErr" />
      <button class="btn primary endBtn" type="button" @click="$emit('back')">{{ copy.back }}</button>
      <p v-if="variant !== 'nice'" class="endHint">
        <template v-if="isAdmin">
          Still failing after a retry? Check your pool numbers in
          <RouterLink to="/settings/phone#numbers" class="endHintLink">Phone settings</RouterLink>.
        </template>
        <template v-else>
          Still failing after a retry? Ask an admin to check the team’s pool numbers.
        </template>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import SessionStatTiles from './SessionStatTiles.vue'
import ErrorDetailBox from './ErrorDetailBox.vue'
import { useCurrentUser } from '../../../services/user'

const props = defineProps<{
  variant: 'error_before' | 'error_during' | 'nice'
  stats: { dialed: number; connected: number; skipped: number; noAnswer: number; failed: number }
  errorDetail: string | null
}>()
defineEmits<{ (e: 'back'): void }>()

const { isAdmin } = useCurrentUser()

const copy = computed(() => {
  if (props.variant === 'nice') {
    return {
      title: 'Nice run.',
      sub: 'You walked the signal-qualified queue. Raven logged every disposition and synced it back automatically.',
      back: 'Back to queue',
    }
  }
  if (props.variant === 'error_during') {
    return {
      title: 'Error during run.',
      sub: 'The dialer dropped part-way through — these calls went through and were logged before it failed. The rest of the queue wasn’t dialed.',
      back: 'Back to queue & retry',
    }
  }
  return {
    title: 'We couldn’t start the dialer.',
    sub: 'The call never got off the ground — usually the dialer isn’t fully connected yet, or its phone credentials need a refresh. Nothing was dialed, so no one was actually called.',
    back: 'Back to queue & retry',
  }
})
</script>

<style scoped>
.endScreen { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; padding: var(--space-7); overflow-y: auto; }
.endInner { display: flex; flex-direction: column; align-items: center; text-align: center; gap: var(--space-4); max-width: 460px; width: 100%; }
.endIcon { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 999px; }
.endIcon.ok { background: var(--color-bg-subtle); color: var(--color-accent); }
.endIcon.err { background: var(--color-bg-subtle); color: var(--color-danger); }
.endTitle { font-family: var(--font-display); font-size: var(--fs-xl); font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.endSub { font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }
.endTiles { margin-top: var(--space-2); }
.endErr { width: 100%; margin-top: var(--space-2); }
.endBtn { margin-top: var(--space-3); padding: var(--space-2) var(--space-6); }
.endHint { font-size: var(--fs-xs); color: var(--color-text-tertiary); max-width: 380px; }
.endHintLink { color: var(--color-accent); font-weight: var(--fw-medium); }
.endHintLink:hover { text-decoration: underline; }
</style>
